import React, { ChangeEvent, useEffect, useContext, useState } from 'react';
import { Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import capaSisdaf from '../../../assets/capa_sisdaf.png';
import logoGov from '../../../assets/logo_govbr.svg';
import logoMS from '../../../assets/logo_ministerio_saude.png';
import logoSisdaf from '../../../assets/logo_sisaf.png';
import Button from '../../../components/Button';
import GovbrSignInButton from '../../../components/Button/govbrButton';
import ContainerBase from '../../../components/ContainerBase';
import { CustomSwal } from '../../../components/CustomFeedback/CustomSwal';
import CustomInputMask from '../../../components/CustomInputs/CustomInputMask';
import CustomInputPassword from '../../../components/CustomInputs/CustomInputPassword';
import Toast from '../../../components/Toast';
import { AuthContext } from '../../../modules/contexts/Auth/AuthContext';
import { useGovBrAuth } from '../../../modules/hooks/useGovBrAuth';
import GlobalStyle from '../../../utils/globalStyles';
import { swal } from '../../../utils/swal';
import { versaoAtualSisaf } from '../AppDAF/versaoSisaf';

const ContainerLogin = styled.div`
  display: grid;
  grid-template-columns: 75% 25%;
  width: 90%;
  height: 85%;

  align-self: center;
  margin: 0 auto;
  border-radius: 20px 20px 30px 20px;
`;
const ImageContainer = styled.div`
  background: url(${capaSisdaf}) no-repeat top;
  background-size: cover;
  border-radius: 20px 0px 0px 20px;
  opacity: 1;
  height: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 2vh;
`;
const LogoMS = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    height: 9vh;
    border-radius: 10px;
    width: auto;
  }
`;
const LoginSection = styled.div`
  /* padding: 10px; */
  display: grid;
  grid-template-rows: 40% 60%;
  width: 100%;
  align-items: center;
  justify-content: center;
  border-radius: 0 20px 20px 0;
  background-color: #ffffff;
`;

const LogoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const LogoSistema = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  img {
    height: 18vh;
    width: auto;
  }
`;

const LoginFormArea = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* justify-content: center; */
  width: 100%;
  height: 100%;
`;

function readCookie(name: string): string | null {
  const rx = new RegExp('(?:^|; )' + encodeURIComponent(name) + '=([^;]*)');
  const m = document.cookie.match(rx);
  return m ? decodeURIComponent(m[1]) : null;
}
function clearCookie(name: string) {
  document.cookie = `${encodeURIComponent(name)}=; Max-Age=0; path=/`;
}

export const Login = () => {
  //console.log('ENABLE_BASIC_LOGIN =', process.env.ENABLE_BASIC_LOGIN);
  //console.log('ENABLE_GOVBR_LOGIN =', process.env.ENABLE_GOVBR_LOGIN);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ cpf: '', senha: '' });
  const [submitting, setSubmitting] = useState(false);
  const [loginStatus, setLoginStatus] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>(
    'info',
  );

  const authContext = useContext(AuthContext);
  if (!authContext)
    throw new Error('Login deve ser usado dentro de um AuthProvider');
  const { signin } = authContext;

  // Carrega configuração do backend
  const [config, setConfig] = useState<{ enableGovbr: boolean } | null>(null);

  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    fetch(`${base}/api/auth/config`)
      .then((r) => r.json())
      .then((data) => {
        console.log('Configuração carregada:', data);
        setConfig(data);
      })
      .catch((err) => {
        console.error('Erro ao carregar configuração:', err);
        setConfig({ enableGovbr: false });
      });
  }, []);

  // Gov.br
  useGovBrAuth(setLoginStatus, setToastType, setShowToast);

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info',
  ) => {
    setLoginStatus(message);
    setToastType(type);
    setShowToast(true);
  };

  useEffect(() => {
    if (showToast) {
      const t = setTimeout(() => setShowToast(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showToast]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formattedValue = name === 'cpf' ? value.replace(/\D/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const doLogin = async () => {
    if (submitting) return;

    const cpfLimpo = (formData.cpf || '').replace(/\D/g, '');
    const senha = formData.senha || '';

    const missingFields: string[] = [];
    if (!cpfLimpo) missingFields.push('CPF');
    if (!senha) missingFields.push('Senha');

    if (missingFields.length > 0) {
      CustomSwal.show({
        title: 'Campos Obrigatórios',
        html: `Por favor, preencha os seguintes campos: <b>${missingFields.join(
          ', ',
        )}</b>.`,
        type: 'error',
        confirmText: 'Fechar',
      });
      return;
    }

    if (cpfLimpo.length !== 11) {
      return swal('warning', 'Atenção', 'CPF deve ter 11 dígitos!');
    }

    setSubmitting(true);

    try {
      await signin(cpfLimpo, senha);
      navigate('/daf/home/inicio', { replace: true });
    } catch (error: any) {
      const status = error?.response?.status || error?.status;
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao autenticar. Tente novamente.';

      if (status === 401 || status === 403) {
        CustomSwal.show({
          title: 'Erro',
          html: 'Usuário ou senha inválidos.',
          type: 'error',
          confirmText: 'Fechar',
        });
        return;
      }

      return swal('error', 'Erro', msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGovBrLogin = () => {
    setSubmitting(true);
    setLoginStatus('Redirecionando para Gov.br...');

    const govBrUrl = `${
      process.env.REACT_APP_API_URL || 'http://localhost:3001'
    }/api/auth/govbr`;

    window.location.href = govBrUrl;
  };

  useEffect(() => {
    const raw = readCookie('auth_error');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const msg: string =
          parsed?.message ||
          'Solicitação efetuada! Seu acesso ainda não está liberado. Contate o administrador.';
        showNotification(msg, 'error');
      } catch {
        showNotification(
          'Erro inesperado na autenticação Gov.br. Tente novamente mais tarde.',
          'error',
        );
      } finally {
        clearCookie('auth_error');
      }
    }
  }, []);

  return (
    <ContainerBase>
      <GlobalStyle />
      <ContainerLogin>
        <ImageContainer>
          <LogoMS>
            <img src={logoMS} alt="Logo do Ministério da Saúde" />
          </LogoMS>
        </ImageContainer>

        <LoginSection>
          <LogoArea>
            <LogoSistema>
              <img src={logoSisdaf} alt="Logo do SisAF" />
              <span
                style={{
                  fontWeight: 'normal',
                  fontSize: '1rem',
                  color: 'black',
                }}
              >
                {versaoAtualSisaf.interfaceDAF}
              </span>
            </LogoSistema>
          </LogoArea>

          <LoginFormArea>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doLogin();
              }}
              style={{ width: '100%', fontSize: '12px' }}
            >
              <CustomInputMask
                title="CPF"
                mask="999.999.999-99"
                value={formData.cpf}
                onChange={handleChange}
                id="CPF"
                placeholder="CPF"
                name="cpf"
                textAlign="center"
                disabled={submitting}
                data-testid="input-CPF"
              />

              <CustomInputPassword
                title="Senha"
                value={formData.senha}
                onChange={handleChange}
                id="password"
                placeholder="Senha"
                name="senha"
                textAlign="center"
                disabled={submitting}
                data-testid="input-password"
                onForgotPassword={() => navigate('/esqueci-senha')}
                onLogin={doLogin}
                showLoginButton
                showForgotPassword
              />
            </form>
            <div
              style={{
                marginTop: '30px',
                width: '100%',
                display: 'flex',
                gap: '10px',
                flexDirection: 'column',
              }}
            >
              {/* #Esqueci minha senha*/}
              <div className="d-grid gap-2">
                <Button
                  label="Solicitar vinculação"
                  onClick={() => navigate('/solicitar-vinculo')}
                  disabled={submitting}
                  data-testid="button-solicitar-vinculo"
                />
              </div>

              {/* #Me cadastrar*/}
              <div className="d-grid gap-2">
                <Button
                  botaoAmarelo
                  label="Me cadastrar"
                  onClick={() => navigate('/cadastrar')}
                  disabled={submitting}
                  data-testid="button-register"
                />
              </div>
            </div>
            <div
              className="d-grid gap-2"
              style={{ marginTop: '20px', width: '100%' }}
            >
              <GovbrSignInButton
                emphasis="custom"
                contentType="image"
                imageSrc={logoGov}
                imageAlt="gov.br"
                onClick={handleGovBrLogin}
                disabled={submitting}
              />
            </div>
          </LoginFormArea>
        </LoginSection>
      </ContainerLogin>

      <Toast
        show={showToast}
        onClose={() => {
          setShowToast(false);
          setLoginStatus('');
        }}
        mensagem={loginStatus}
      />
    </ContainerBase>
  );
};
